import { first } from 'lodash';
import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import gql from 'graphql-tag';
import { MutationFn } from 'react-apollo';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import {
  ReverifyCreditCardMutation,
  core_apimessages_CreditCard as CreditCard,
} from '../schemas/rql';
import { CSEName, useAdyenCard } from './adyen_card_hook';
import { reverbToAdyenCardType } from '../lib/adyen';
import FormGroup from '@reverbdotcom/commons/src/components/form_group';
import { RCButton } from '@reverbdotcom/cadence/components';
import { useCheckoutDisplay } from './checkoutDisplayContext';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';

interface ExternalProps {
  creditCard: CreditCard;
  trackEvent?: () => void;
}

interface MutationProps {
  reverifyCreditCard: MutationFn<ReverifyCreditCardMutation.Mutation, ReverifyCreditCardMutation.Variables>;
}

type Props = ExternalProps & MutationProps;

const cseName: CSEName = 'encryptedSecurityCode';

export function CheckoutVerifyCardCVV({
  creditCard,
  reverifyCreditCard,
  trackEvent = null,
}: Props) {
  const [mutate, { loading, errors }] = useMutation(reverifyCreditCard);
  const checkoutDisplay = useCheckoutDisplay();
  const submissionError = first(errors) || {};
  const { focus, cardState, errors: formErrors } = useAdyenCard({
    selector: '[data-verify-cvv-field]',
    cardType: reverbToAdyenCardType(creditCard.cardType),
  });

  const isSubmitDisabled = checkoutDisplay.isLoading || loading || !cardState.isValid;
  const errorMessage = submissionError.message || formErrors[cseName]?.errorI18n;

  const cvvClassNames = classNames('adyen-field', {
    'adyen-field--focused': focus?.fieldType === cseName,
    'adyen-field--error': !!errorMessage,
  });

  function refetchOptions() {
    if (!checkoutDisplay.layoutQueryName) { return {}; }

    return {
      refetchQueries: [checkoutDisplay.layoutQueryName],
      awaitRefetchQueries: true,
    };
  }

  async function submitCardCVV(event: any) {
    event.preventDefault();

    if (trackEvent) {
      trackEvent();
    }


    checkoutDisplay.setLoading(true);

    await reverify();

    checkoutDisplay.setLoading(false);
  }

  async function reverify() {
    const tokenizedFieldsJson = JSON.stringify(cardState.data.paymentMethod);

    return mutate({
      variables: {
        input: {
          creditCardId: creditCard.id,
          tokenizedFields: tokenizedFieldsJson,
        },
      },
      ...refetchOptions(),
    });
  }

  return (
    <FormGroup
      label={I18n.t('discovery.creditCardForm.pleaseReverifyCvv')}
      inputName="cardCVV"
      helpText={I18n.t('discovery.creditCardForm.cvvLocation')}
      errorText={errorMessage}
    >
      <div className="d-flex fx-align-start">
        <div data-verify-cvv-field className="width-rem-10 mr-1">
          <div className={cvvClassNames} data-cse={cseName} />
        </div>
        <RCButton
          variant="primary"
          onClick={submitCardCVV}
          disabled={isSubmitDisabled}
        >
          {I18n.t('discovery.creditCardForm.verifyCvvButton')}
        </RCButton>
      </div>
    </FormGroup>
  );
}

export const withMutation = withGraphql<ExternalProps, ReverifyCreditCardMutation.Mutation, ReverifyCreditCardMutation.Variables>(
  gql`mutation reverifyCreditCardMutation($input: Input_core_apimessages_ReverifyCreditCardRequest) {
    reverifyCreditCard(input: $input) {
      creditCard {
        id
        last4
        cardType
        expirationYear
        expirationMonth
        cardholderName
        needsReverification
        primaryForCheckout
      }
    }
  }`,
  {
    name: 'reverifyCreditCard',
  });

export default withMutation(CheckoutVerifyCardCVV);
